import React, { useState, useEffect, useCallback } from 'react';
import { Breadcrumb, Row, Col, ListGroup, Badge, Pagination, Button, Form, Accordion, InputGroup, OverlayTrigger, Tooltip } from 'react-bootstrap';
import axios from 'axios';
import moment from 'moment';
import Select from 'react-select';
import { getCurrencySymbol } from '../../common_components/choices';
import { getPaginationItems } from '../../common_components/getPaginationItems';
import { toast } from 'react-toastify';
import OfferDetailsModal from './OfferDetailsModal';
import ConfirmationModal from '../../common_components/ConfirmationModal';

const OfferDetails = ({ selectedOffer, setSelectedOffer }) => {
    console.log("Received offer:", JSON.stringify(selectedOffer, null, 2));
    const token = localStorage.getItem('token');
    const userInfoContent = JSON.parse(localStorage.getItem('userInfo'));

    const productPageSize = 5; // Adjust page size as needed
    const [productCurrentPage, setProductCurrentPage] = useState(1);
    const productTotalPages = Math.ceil(selectedOffer.products.length / productPageSize);

    // State for grocery lists
    const [defaultGroceryList, setDefaultGroceryList] = useState(null);


    const [showEditModal, setShowEditModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [productToRemove, setProductToRemove] = useState(null);

    const [availableProducts, setAvailableProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [newProductDetails, setNewProductDetails] = useState({
        min_quantity: 1,
        product_offer_price: '',
        product_offer_price_currency: userInfoContent.preferred_currency || 'EUR',
    });

    const paginatedProducts = selectedOffer.products.slice(
        (productCurrentPage - 1) * productPageSize,
        productCurrentPage * productPageSize
    );

    const handleProductPageChange = (pageNumber) => {
        setProductCurrentPage(pageNumber);
    };

    const fetchAvailableProducts = useCallback(async (searchTerm = '') => {
        try {
            const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-products/`, {
                params: { search: searchTerm },
                headers: { Authorization: `Token ${token}` },
            });
            setAvailableProducts(data.results);
        } catch (error) {
            console.error('Failed to fetch products', error);
        }
    }, [token]);

    const fetchUserGroceryLists = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/household-grocery-lists/`, {
                headers: {
                    'Authorization': `Token ${localStorage.getItem('token')}`
                }
            });
            if (response.data.results.length > 0) {
                const defaultList = response.data.results.find(list => list.is_default === true);
                setDefaultGroceryList(defaultList || response.data.results[0]);
            } else {
                setDefaultGroceryList(null);
                console.warn("No grocery lists found.");
            }
        } catch (error) {
            console.error("Error fetching user grocery lists:", error);
        }
    };

    const fetchUpdatedOffer = async () => {
        try {
            const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-offers/${selectedOffer.id}/`, {
                headers: { Authorization: `Token ${token}` },
            });
            setSelectedOffer(data);
        } catch (error) {
            console.error('Failed to fetch updated offer', error);
        }
    };

    const handleOfferModalShow = () => {
        setShowEditModal(true);
        fetchAvailableProducts();
    };

    const handleEditModalClose = () => {
        setShowEditModal(false);
    };

    const handleProductSelectChange = (selectedOption) => {
        setSelectedProduct(selectedOption);
    };

    const handleProductInputChange = (inputValue) => {
        fetchAvailableProducts(inputValue);
    };

    const handleNewProductDetailChange = (field, value) => {
        setNewProductDetails((prevDetails) => ({
            ...prevDetails,
            [field]: value,
        }));
    };

    const handleAddProduct = async () => {
        if (!selectedProduct) {
            alert("Please select a product");
            return;
        }

        try {
            const payload = {
                offer: selectedOffer.id,
                product: selectedProduct.value,
                min_quantity: newProductDetails.min_quantity,
                product_offer_price: newProductDetails.product_offer_price,
                product_offer_price_currency: userInfoContent.preferred_currency || 'XXX',
            };
            console.log("Sending payload:", payload);
            await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/offer-products/`, payload, {
                headers: { Authorization: `Token ${token}` },
            });
            // Fetch the updated offer
            toast.success('Product added successfully!');
            fetchUpdatedOffer();
            setSelectedProduct(null);
            setNewProductDetails({
                min_quantity: 1,
                product_offer_price: '',
                product_offer_price_currency: '',
            });
        } catch (error) {
            toast.error(error.response.data.message[0]);
        }
    };

    const handleRemoveProduct = async () => {
        if (!productToRemove) return;

        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/offer-products/${productToRemove}/`, {
                headers: { Authorization: `Token ${token}` },
            });
            toast.success('Product removed successfully!');
            fetchUpdatedOffer();
            setShowConfirmModal(false);
            setProductToRemove(null);
        } catch (error) {
            toast.error('Failed to remove product. Could you please try again?');
            console.error('Failed to remove product', error);
        }
    };

    const addProductToGroceryList = async (product) => {
        if (!defaultGroceryList) {
            console.error("No default grocery list found.");
            return;
        }
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/user-grocery-items/`, {
                list_uuid: defaultGroceryList.uuid,
                name: product.product_name,
                quantity: product.min_quantity,
                purchase_price: product.offer_price,
                purchase_price_currency: product.currency,
                product: product.product_id,
                offer: selectedOffer.id
            }, {
                headers: { 'Authorization': `Token ${token}` }
            });
            console.log("Product added to grocery list:", defaultGroceryList.name);
            toast.success(`Product added to ${defaultGroceryList.name}!`);
        } catch (error) {
            toast.error("Error adding product to grocery list:", error);
            console.error("Error adding product to grocery list:", error);
        }
    };


    useEffect(() => {
        fetchAvailableProducts();
        fetchUserGroceryLists();
    }, [fetchAvailableProducts]);

    return (
        <>
            <Breadcrumb>
                <Breadcrumb.Item onClick={() => setSelectedOffer(null)} style={{ cursor: 'pointer' }}>Offers</Breadcrumb.Item>
                <Breadcrumb.Item active>{selectedOffer.name}</Breadcrumb.Item>
            </Breadcrumb>
            <Row>
                <Col>
                    <div className="d-flex align-items-center mb-2">
                        <h4 style={{ marginBottom: '0' }}>{selectedOffer.name}</h4>
                        <Badge bg={selectedOffer.status === 'ACTIVE' ? 'success' : 'secondary'} style={{ marginLeft: '10px' }}>{selectedOffer.status}</Badge>
                        <i
                            className={selectedOffer.is_owner ? "bi bi-pencil-square ml-2" : "bi bi-info-circle ml-2"}
                            style={{ cursor: 'pointer', marginLeft: '8px' }}
                            onClick={handleOfferModalShow}
                        ></i>
                    </div>
                    <p className="mb-2">{selectedOffer.description}</p>
                    <p className="mb-2">Store: {selectedOffer.store_brand_name}</p>
                    <p>
                        Dates:
                        {selectedOffer.start_date ? moment(selectedOffer.start_date).format('DD-MMM-YYYY') : 'No start date available'}
                        -
                        {selectedOffer.end_date ? moment(selectedOffer.end_date).format('DD-MMM-YYYY') : 'No end date available'}
                    </p>
                </Col>
            </Row>
            <Row>
                <ListGroup>
                    {/* Show Accordian only if selectedOffer.is_owner is true */}
                    {selectedOffer.is_owner && (
                        <Accordion defaultActiveKey="0">
                            <Accordion.Item eventKey="0">
                                <Accordion.Header>Add Products</Accordion.Header>
                                <Accordion.Body>
                                    <Form.Group controlId="formProductSelect">
                                        <Row className="align-items-center">
                                            <Col md={6}>
                                                <Select
                                                    options={availableProducts.map(product => ({
                                                        value: product.id,
                                                        label: product.name,
                                                    }))}
                                                    onChange={handleProductSelectChange}
                                                    onInputChange={handleProductInputChange}
                                                    value={selectedProduct}
                                                />
                                            </Col>
                                            <Col md={2}>
                                                <Form.Control
                                                    type="number"
                                                    placeholder="Min Qty"
                                                    value={newProductDetails.min_quantity}
                                                    onChange={(e) => handleNewProductDetailChange('min_quantity', e.target.value)}
                                                />
                                            </Col>
                                            <Col md={3}>
                                                <InputGroup>
                                                    <InputGroup.Text>{getCurrencySymbol(newProductDetails.product_offer_price_currency)}</InputGroup.Text>
                                                    <Form.Control
                                                        type="number"
                                                        placeholder="Offer Price"
                                                        value={newProductDetails.product_offer_price}
                                                        onChange={(e) => handleNewProductDetailChange('product_offer_price', e.target.value)}
                                                    />
                                                </InputGroup>
                                            </Col>
                                            <Col md={1}>
                                                <Button variant="success" onClick={handleAddProduct}>
                                                    <i className="bi bi-plus-lg"></i>
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Form.Group>
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    )}
                    {paginatedProducts.map((product) => (
                        <ListGroup.Item key={product.through_id}>
                            <div className="d-flex justify-content-between">
                                <div className="offer-details">
                                    {selectedOffer.is_owner === true ? (
                                        <i
                                            className="bi bi-dash-circle"
                                            style={{ cursor: 'pointer', marginRight: '10px' }}
                                            onClick={() => {
                                                setProductToRemove(product.through_id);
                                                setShowConfirmModal(true);
                                            }}
                                        ></i>
                                    ) : null}
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id={`tooltip-${product.id}`}>{product.brand}</Tooltip>}
                                    >
                                        <Badge style={{ fontSize: '12px', minWidth: '100px', marginRight: '10px', display: 'inline-flex', alignItems: 'center' }}>{product.brand}</Badge>
                                    </OverlayTrigger>
                                    <h6>{product.product_name}</h6>
                                </div>
                                <div className="offer-details">
                                    {product.normal_price && (
                                        <p className="normal-price">{product.normal_price} {getCurrencySymbol(product.currency)}</p>
                                    )}
                                    <p>{product.offer_price} {getCurrencySymbol(product.currency)}</p>
                                    <p>(Min Qty: {product.min_quantity})</p>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id={`tooltip-${product.id}`}>Add to default grocery list</Tooltip>}
                                    >
                                        <i
                                            className="bi bi-plus-circle"
                                            style={{ cursor: 'pointer', marginLeft: '8px' }}
                                            onClick={() => addProductToGroceryList(product)}
                                        ></i>
                                    </OverlayTrigger>
                                </div>
                            </div>
                        </ListGroup.Item>
                    ))}
                </ListGroup>
            </Row>
            <div className="fixed-pagination">
                <Pagination>
                    <Pagination.Prev
                        onClick={() => handleProductPageChange(productCurrentPage - 1)}
                        disabled={productCurrentPage === 1}
                    />
                    {getPaginationItems(productCurrentPage, productTotalPages, 11, handleProductPageChange)}
                    <Pagination.Next
                        onClick={() => handleProductPageChange(productCurrentPage + 1)}
                        disabled={productCurrentPage === productTotalPages}
                    />
                </Pagination>
            </div>

            {/* Edit Modal */}
            <OfferDetailsModal
                show={showEditModal}
                handleClose={handleEditModalClose}
                offer={selectedOffer}
                token={token}
                fetchUpdatedOffer={fetchUpdatedOffer}
            />

            {/* Confirmation Modal */}
            <ConfirmationModal
                show={showConfirmModal}
                onHide={() => setShowConfirmModal(false)}
                onConfirm={handleRemoveProduct}
                title="Confirm Product Removal"
                body="Are you sure you want to remove this product from the offer?"
            />
        </>
    );
};

export default OfferDetails;
